import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, DollarSign, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const emptyProposal = {
  matricula: 0, mensalidade: 0, period_months: 3,
  accommodation: "individual",
  evaluation_cost: 0, exams_cost: 0, medication_cost: 0,
  transport_cost: 0, enxoval_cost: 0,
  other_costs: [], conditions: "", validity_days: 7, notes: "",
};

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const Proposal = () => {
  const { caseId } = useParams();
  const { user } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [decisionDraft, setDecisionDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const isConsultor = user?.role === "consultor" || user?.role === "admin";
  const isFamily = user?.role === "familia";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/proposal`);
      if (data.exists === false) setProposal({ ...emptyProposal, exists: false, case_protocol: data.case_protocol });
      else setProposal(data);
    } catch { toast.error("Erro ao carregar proposta"); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (!proposal) return <div className="p-10 text-slate-500">Carregando…</div>;

  const set = (k, v) => setProposal({ ...proposal, [k]: v });

  const others = proposal.other_costs || [];
  const setOther = (i, k, v) => set("other_costs", others.map((o, j) => j === i ? { ...o, [k]: v } : o));
  const addOther = () => set("other_costs", [...others, { label: "", value: 0 }]);
  const removeOther = (i) => set("other_costs", others.filter((_, j) => j !== i));

  const totals = {
    total_no_ato:
      Number(proposal.matricula || 0) + Number(proposal.evaluation_cost || 0) +
      Number(proposal.exams_cost || 0) + Number(proposal.medication_cost || 0) +
      Number(proposal.transport_cost || 0) + Number(proposal.enxoval_cost || 0) +
      others.reduce((s, o) => s + Number(o.value || 0), 0),
    total_mensalidades: Number(proposal.mensalidade || 0) * Number(proposal.period_months || 1),
  };
  totals.total_geral = totals.total_no_ato + totals.total_mensalidades;

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/proposal`, proposal);
      toast.success(`Proposta v${(proposal.version || 0) + 1} salva`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setBusy(false); }
  };

  const decide = async (decision) => {
    if (!window.confirm(`Confirmar: ${decision === "accepted" ? "Aceitar" : decision === "adjust_requested" ? "Solicitar ajuste" : "Recusar"} a proposta?`)) return;
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/proposal/decide`, { decision, comment: decisionDraft });
      toast.success("Decisão registrada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const decision = proposal.family_decision || {};
  const decisionBadge = {
    pending: { label: "Aguardando decisão", color: "bg-slate-100 text-slate-700", icon: null },
    accepted: { label: "Aceita pela Família", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    adjust_requested: { label: "Ajuste solicitado", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle },
    refused: { label: "Recusada pela Família", color: "bg-red-100 text-red-800", icon: XCircle },
  }[decision.status || "pending"];

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="proposal-page">
      <Link to={isConsultor ? `/consultor/caso/${caseId}` : `/caso/${caseId}`}
            className="text-sm text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Voltar ao caso
      </Link>

      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
            {proposal.case_protocol ? `Protocolo ${proposal.case_protocol}` : "Caso RAF"}
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">
            Proposta Oficial <span className="text-slate-400 text-xl">v{proposal.version || "—"}</span>
          </h1>
        </div>
        {decisionBadge && (
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${decisionBadge.color}`}>
            {decisionBadge.icon && <decisionBadge.icon className="w-3.5 h-3.5" />}
            {decisionBadge.label}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <TotalCard label="Total no ato" value={brl(totals.total_no_ato)} />
        <TotalCard label={`Mensalidades (${proposal.period_months || 1}× ${brl(proposal.mensalidade)})`} value={brl(totals.total_mensalidades)} />
        <TotalCard label="Total geral" value={brl(totals.total_geral)} accent />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Money label="Matrícula" value={proposal.matricula} onChange={(v) => set("matricula", v)} readOnly={!isConsultor} testid="p-matricula" />
        <Money label="Mensalidade" value={proposal.mensalidade} onChange={(v) => set("mensalidade", v)} readOnly={!isConsultor} testid="p-mensalidade" />
        <Field label="Período (meses)" testid="p-period">
          <input type="number" min={1} max={24} value={proposal.period_months}
                 disabled={!isConsultor} onChange={(e) => set("period_months", Number(e.target.value))}
                 className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62] disabled:bg-slate-50" />
        </Field>
        <Field label="Acomodação" testid="p-accommodation">
          <select value={proposal.accommodation} disabled={!isConsultor}
                  onChange={(e) => set("accommodation", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-50">
            <option value="individual">Individual</option>
            <option value="compartilhada">Compartilhada</option>
            <option value="enfermaria">Enfermaria</option>
          </select>
        </Field>
        <Money label="Avaliação" value={proposal.evaluation_cost} onChange={(v) => set("evaluation_cost", v)} readOnly={!isConsultor} testid="p-eval" />
        <Money label="Exames" value={proposal.exams_cost} onChange={(v) => set("exams_cost", v)} readOnly={!isConsultor} testid="p-exams" />
        <Money label="Medicamentos" value={proposal.medication_cost} onChange={(v) => set("medication_cost", v)} readOnly={!isConsultor} testid="p-med" />
        <Money label="Transporte" value={proposal.transport_cost} onChange={(v) => set("transport_cost", v)} readOnly={!isConsultor} testid="p-transport" />
        <Money label="Enxoval" value={proposal.enxoval_cost} onChange={(v) => set("enxoval_cost", v)} readOnly={!isConsultor} testid="p-enxoval" />
        <Field label="Validade da proposta (dias)" testid="p-validity">
          <input type="number" min={1} max={90} value={proposal.validity_days}
                 disabled={!isConsultor} onChange={(e) => set("validity_days", Number(e.target.value))}
                 className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62] disabled:bg-slate-50" />
        </Field>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-500">Outras despesas</div>
          {isConsultor && <button onClick={addOther} data-testid="p-other-add" className="text-xs text-[#0B2B62] font-semibold hover:underline">+ Adicionar</button>}
        </div>
        {others.length === 0 && <div className="text-sm text-slate-400">— nenhum outro custo —</div>}
        <ul className="space-y-2">
          {others.map((o, i) => (
            <li key={`other-${i}`} className="grid grid-cols-[1fr_140px_auto] gap-2 items-center">
              <input value={o.label} disabled={!isConsultor} onChange={(e) => setOther(i, "label", e.target.value)}
                     placeholder="Descrição" data-testid={`p-other-label-${i}`}
                     className="px-2.5 py-1.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62] disabled:bg-slate-50" />
              <input type="number" step="0.01" value={o.value} disabled={!isConsultor}
                     onChange={(e) => setOther(i, "value", Number(e.target.value))}
                     data-testid={`p-other-val-${i}`}
                     className="px-2.5 py-1.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62] disabled:bg-slate-50" />
              {isConsultor && <button onClick={() => removeOther(i)} className="text-xs text-red-500 hover:text-red-700">Remover</button>}
            </li>
          ))}
        </ul>
      </div>

      <Section title="Condições" testid="p-conditions" value={proposal.conditions} readOnly={!isConsultor} onChange={(v) => set("conditions", v)} />
      <Section title="Observações internas do consultor" testid="p-notes" value={proposal.notes} readOnly={!isConsultor} onChange={(v) => set("notes", v)} />

      {isConsultor && (
        <div className="flex items-center gap-3 pt-2 border-t border-slate-200 mt-6">
          <button data-testid="p-save" onClick={save} disabled={busy}
            className="px-5 py-2.5 bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> {proposal.exists === false ? "Emitir proposta" : `Salvar v${(proposal.version || 0) + 1}`}
          </button>
        </div>
      )}

      {isFamily && proposal.version && (decision.status === "pending" || !decision.status) && (
        <div className="mt-8 p-5 bg-slate-50 border border-slate-200 rounded-xl">
          <h3 className="font-display font-semibold text-lg text-[#0B2B62]">Sua decisão</h3>
          <p className="text-sm text-slate-600 mt-1">Analise a proposta com calma. Se algo precisar mudar, envie um comentário e solicite ajuste — o consultor RAF revisará.</p>
          <textarea rows={3} value={decisionDraft} onChange={(e) => setDecisionDraft(e.target.value)}
            data-testid="p-decision-comment"
            placeholder="Comentário (opcional)"
            className="mt-3 w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="p-accept" onClick={() => decide("accepted")} disabled={busy}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
              Aceitar proposta
            </button>
            <button data-testid="p-adjust" onClick={() => decide("adjust_requested")} disabled={busy}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md text-sm font-semibold hover:bg-yellow-600 disabled:opacity-50">
              Solicitar ajuste
            </button>
            <button data-testid="p-refuse" onClick={() => decide("refused")} disabled={busy}
              className="px-4 py-2 border border-red-500 text-red-600 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-50">
              Recusar
            </button>
          </div>
        </div>
      )}
      {decision.decided_at && (
        <div className="mt-6 text-xs text-slate-500">
          Decisão em {new Date(decision.decided_at).toLocaleString("pt-BR")}
          {decision.comment ? ` — "${decision.comment}"` : ""}
        </div>
      )}
    </div>
  );
};

const TotalCard = ({ label, value, accent }) => (
  <div className={`p-4 border rounded-xl ${accent ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 bg-white"}`}>
    <div className={`text-xs uppercase tracking-widest ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</div>
    <div className={`font-display font-bold text-2xl mt-1 ${accent ? "text-white" : "text-[#0B2B62]"}`}>{value}</div>
  </div>
);

const Field = ({ label, children, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1" data-testid={`${testid}-label`}>{label}</label>
    {children}
  </div>
);

const Money = ({ label, value, onChange, readOnly, testid }) => (
  <Field label={label} testid={testid}>
    <input type="number" step="0.01" min={0} value={value} disabled={readOnly}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62] disabled:bg-slate-50" />
  </Field>
);

const Section = ({ title, value, readOnly, onChange, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    {readOnly ? (
      <p className="text-slate-700 whitespace-pre-line text-sm leading-relaxed">{value || <span className="text-slate-400">— não preenchido —</span>}</p>
    ) : (
      <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
    )}
  </div>
);

export default Proposal;
